import React, { useEffect, useState, useRef } from "react";
import { Routes, Route, Link, NavLink, useNavigate, Navigate } from "react-router-dom";
import api, { formatTL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { toast } from "sonner";
import { LayoutDashboard, Package, Tags, ShoppingCart, Users, Settings, LogOut, Upload, Trash2, Edit, Plus, Sparkles, Tv, Image as ImageIcon, Search as SearchIcon, X, TrendingUp, FileText, FolderOpen, Globe } from "lucide-react";
import { MediaLibrary, ImagePicker } from "@/components/MediaLibrary";
import WatchImport from "@/pages/admin/WatchImport";
import ProductsCatalog from "@/pages/admin/ProductsCatalog";
import HomepageManager from "@/pages/admin/HomepageManager";

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { lang } = useLang();

  const nav = [
    { to: "/admin", icon: LayoutDashboard, label: lang === "tr" ? "Panel" : "Dashboard", end: true },
    { to: "/admin/products", icon: Package, label: lang === "tr" ? "Ürünler" : "Products" },
    { to: "/admin/categories", icon: Tags, label: lang === "tr" ? "Kategoriler" : "Categories" },
    { to: "/admin/orders", icon: ShoppingCart, label: lang === "tr" ? "Siparişler" : "Orders" },
    { to: "/admin/customers", icon: Users, label: lang === "tr" ? "Müşteriler" : "Customers" },
    { to: "/admin/imports", icon: Globe, label: lang === "tr" ? "Saat İçe Aktarma" : "Watch Import" },
    { to: "/admin/rates", icon: Tv, label: lang === "tr" ? "Döviz & Marjlar" : "Rates & Margins" },
    { to: "/admin/homepage", icon: Settings, label: lang === "tr" ? "Anasayfa" : "Homepage" },
    { to: "/admin/media", icon: FolderOpen, label: lang === "tr" ? "Medya Kütüphanesi" : "Media Library" },
    { to: "/admin/site", icon: ImageIcon, label: lang === "tr" ? "Site Ayarları" : "Site Settings" },
    { to: "/admin/seo", icon: FileText, label: lang === "tr" ? "SEO Ayarları" : "SEO Settings" },
  ];

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-bone flex">
      <aside className="w-64 bg-[#0A0A0A] text-white p-6 sticky top-0 h-screen flex flex-col">
        <Link to="/" className="block mb-10">
          <div className="font-heading text-2xl">Uzungöl</div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C8A96A] mt-0.5">Admin Panel</div>
        </Link>
        <nav className="space-y-1 flex-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.to.split("/").pop() || "dashboard"}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-[#C8A96A] text-white" : "text-[#A1A1AA] hover:text-white hover:bg-white/5"}`}>
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-[#27272A] pt-4 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{user?.email}</div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C8A96A]">{user?.role}</div>
          <button onClick={async () => { await logout(); navigate("/"); }} data-testid="admin-logout"
            className="flex items-center gap-2 text-xs text-[#A1A1AA] hover:text-white mt-3">
            <LogOut size={14} /> {lang === "tr" ? "Çıkış" : "Logout"}
          </button>
        </div>
      </aside>
      <main className="flex-1 p-10 overflow-x-auto">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="products" element={<ProductsCatalog />} />
          <Route path="categories" element={<CategoriesAdmin />} />
          <Route path="orders" element={<OrdersAdmin />} />
          <Route path="customers" element={<CustomersAdmin />} />
          <Route path="imports" element={<WatchImport />} />
          <Route path="rates" element={<RatesAdmin />} />
          <Route path="homepage" element={<HomepageManager />} />
          <Route path="homepage/legacy" element={<HomepageAdmin />} />
          <Route path="site" element={<SiteAdmin />} />
          <Route path="seo" element={<SeoAdmin />} />
          <Route path="media" element={<MediaLibrary />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function Dashboard() {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data));
    api.get("/admin/dashboard/stats").then((r) => setStats(r.data));
  }, []);
  if (!data) return <div>Loading...</div>;

  const cards = [
    { label: lang === "tr" ? "Toplam Sipariş" : "Total Orders", val: data.total_orders, color: "#222222" },
    { label: lang === "tr" ? "Bekleyen Sipariş" : "Pending Orders", val: data.pending_orders, color: "#EF4444" },
    { label: lang === "tr" ? "Toplam Müşteri" : "Customers", val: data.total_customers, color: "#10B981" },
    { label: lang === "tr" ? "Toplam Gelir" : "Total Revenue", val: formatTL(data.total_revenue), color: "#C8A96A", isString: true },
    { label: lang === "tr" ? "Toplam Ürün" : "Total Products", val: stats?.total_products ?? "—", color: "#0A0A0A" },
    { label: lang === "tr" ? "Yayında" : "Published", val: stats?.published_products ?? "—", color: "#16A34A" },
    { label: lang === "tr" ? "Taslak" : "Draft", val: stats?.draft_products ?? "—", color: "#71717A" },
    { label: lang === "tr" ? "Stokta Yok" : "Out of Stock", val: stats?.out_of_stock_products ?? "—", color: "#EF4444" },
    { label: lang === "tr" ? "Çok Yakında" : "Coming Soon", val: stats?.coming_soon_products ?? "—", color: "#C8A96A" },
    { label: lang === "tr" ? "Kategoriler" : "Categories", val: stats?.total_categories ?? data.categories ?? "—", color: "#52525B" },
    { label: lang === "tr" ? "Markalar" : "Brands", val: stats?.total_brands ?? "—", color: "#52525B" },
    { label: lang === "tr" ? "Son İçe Aktarma" : "Last Import", val: stats?.last_import_at ? new Date(stats.last_import_at).toLocaleDateString("tr-TR") : "—", color: "#C8A96A", isString: true },
  ];
  const monthly = data.monthly_revenue || [];
  const maxR = Math.max(1, ...monthly.map((m) => m.revenue));

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-heading text-4xl mb-2">{lang === "tr" ? "Yönetim Paneli" : "Dashboard"}</h1>
      <p className="text-sm text-[#52525B] mb-10">{lang === "tr" ? "Genel bakış" : "Overview"}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-10">
        {cards.map((c, i) => (
          <div key={c.label} data-testid={`stats-card-${i}`} className="bg-white border border-border p-5">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#52525B]">{c.label}</p>
            <p className="font-finance text-2xl mt-3" style={{ color: c.color }}>{c.isString ? c.val : (c.val ?? "—")}</p>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-[1fr_400px] gap-6">
        <div className="bg-white border border-border p-6">
          <h3 className="font-heading text-xl mb-1">{lang === "tr" ? "Aylık Gelir" : "Monthly Revenue"}</h3>
          <p className="text-xs text-[#52525B] mb-6">{lang === "tr" ? "Son 12 ay" : "Last 12 months"}</p>
          {monthly.length === 0 ? (
            <p className="text-sm text-[#71717A]">{lang === "tr" ? "Henüz veri yok" : "No data yet"}</p>
          ) : (
            <div className="flex items-end gap-2 h-48">
              {monthly.map((m) => {
                const h = (m.revenue / maxR) * 100;
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-1 group">
                    <div className="flex-1 w-full flex items-end">
                      <div className="w-full bg-[#C8A96A] hover:bg-[#A6864A] transition-all" style={{ height: `${h}%`, minHeight: "2px" }} title={formatTL(m.revenue)}></div>
                    </div>
                    <span className="text-[9px] text-[#71717A] font-finance">{m.month.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="bg-white border border-border p-6">
          <h3 className="font-heading text-xl mb-4">{lang === "tr" ? "Son Siparişler" : "Recent Orders"}</h3>
          {(data.recent_orders || []).length === 0 ? (
            <p className="text-sm text-[#71717A]">{lang === "tr" ? "Henüz sipariş yok" : "No orders yet"}</p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {data.recent_orders.map((o) => (
                <li key={o.id} className="py-2 flex justify-between gap-3">
                  <div className="truncate">
                    <p className="font-finance text-xs">#{o.id.slice(0, 8)}</p>
                    <p className="text-[#52525B] text-xs truncate">{o.user_email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-finance">{formatTL(o.total)}</p>
                    <p className="text-[10px] uppercase tracking-wider text-[#C8A96A]">{o.status}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="bg-white border border-border p-6 mt-6">
        <h3 className="font-heading text-xl mb-4">{lang === "tr" ? "Çok Satanlar" : "Best Sellers"}</h3>
        {(data.best_selling || []).length === 0 ? (
          <p className="text-sm text-[#71717A]">{lang === "tr" ? "Henüz satış verisi yok" : "No sales data yet"}</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {data.best_selling.map((p) => (
              <div key={p.product_id} className="border border-border p-3 flex gap-3">
                {p.image && <img src={p.image} alt="" className="w-14 h-14 object-cover bg-[#FAF8F3]" />}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{p.name_tr || "—"}</p>
                  <p className="text-[10px] text-[#71717A] font-finance">{p.sku}</p>
                  <p className="font-finance text-xs mt-1">{p.qty} adet · {formatTL(p.revenue)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function flattenCategoryTree(list) {
  const parents = list.filter((c) => !c.parent_id).sort((a, b) => (a.order || 0) - (b.order || 0));
  const result = [];
  parents.forEach((p) => {
    result.push({ cat: p, depth: 0 });
    const children = list.filter((c) => c.parent_id === p.id).sort((a, b) => (a.order || 0) - (b.order || 0));
    children.forEach((c) => result.push({ cat: c, depth: 1 }));
  });
  return result;
}

function CategoriesAdmin() {
  const { lang } = useLang();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { name_tr: "", name_en: "", slug: "", order: 0, image_url: "", parent_id: null };
  const [form, setForm] = useState(empty);

  const reload = () => api.get("/categories").then((r) => setList(r.data));
  useEffect(() => { reload(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, parent_id: form.parent_id || null };
      if (editing) await api.put(`/categories/${editing}`, payload);
      else await api.post("/categories", payload);
      toast.success("OK");
      setForm(empty); setEditing(null); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const move = async (cat, delta) => {
    const newOrder = (cat.order || 0) + delta;
    await api.put(`/categories/${cat.id}/order`, { order: newOrder });
    reload();
  };

  const parents = list.filter((c) => !c.parent_id).sort((a, b) => (a.order || 0) - (b.order || 0));
  const rows = flattenCategoryTree(list);

  return (
    <div data-testid="admin-categories">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-heading text-4xl mb-1">{lang === "tr" ? "Kategoriler" : "Categories"}</h1>
          <p className="text-sm text-[#52525B]">{lang === "tr" ? "Üst kategori ve alt kategori yönetimi · sıralama · gerçek zamanlı yayın" : "Manage categories and subcategories · ordering · real-time sync"}</p>
        </div>
        <button onClick={() => { setForm(empty); setEditing(null); }} data-testid="cat-new-btn"
          className="border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors inline-flex items-center gap-2">
          <Plus size={12} /> {lang === "tr" ? "Yeni Kategori" : "New Category"}
        </button>
      </div>
      <div className="grid lg:grid-cols-[1fr_420px] gap-8">
        <div className="bg-white border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F2EC] text-[10px] uppercase tracking-[0.2em]">
              <tr>
                <th className="text-left px-4 py-3">TR</th>
                <th className="text-left px-4 py-3">EN</th>
                <th className="text-left px-4 py-3">Slug</th>
                <th className="text-center px-4 py-3 w-16">Sıra</th>
                <th className="px-4 py-3 w-44"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ cat, depth }) => (
                <tr key={cat.id} data-testid={`cat-row-${cat.slug}`} className="border-t border-border">
                  <td className="px-4 py-3" style={{ paddingLeft: `${1 + depth * 1.5}rem` }}>
                    {depth > 0 && <span className="text-[#C8A96A] mr-2">↳</span>}
                    {cat.name_tr}
                  </td>
                  <td className="px-4 py-3 text-[#52525B]">{cat.name_en}</td>
                  <td className="px-4 py-3 font-finance text-xs">{cat.slug}</td>
                  <td className="px-4 py-3 font-finance text-xs text-center">{cat.order ?? 0}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => move(cat, -1)} data-testid={`cat-up-${cat.slug}`} className="text-[#52525B] hover:text-[#C8A96A] mx-1" title="Yukarı">↑</button>
                    <button onClick={() => move(cat, 1)} data-testid={`cat-down-${cat.slug}`} className="text-[#52525B] hover:text-[#C8A96A] mx-1" title="Aşağı">↓</button>
                    <button onClick={() => { setForm({ name_tr: cat.name_tr, name_en: cat.name_en, slug: cat.slug, order: cat.order || 0, image_url: cat.image_url || "", parent_id: cat.parent_id || null }); setEditing(cat.id); }} className="text-[#C8A96A] mx-2" title="Düzenle"><Edit size={14} /></button>
                    <button onClick={async () => { if (window.confirm(lang === "tr" ? "Bu kategoriyi ve tüm alt kategorilerini silmek istediğinizden emin misiniz?" : "Delete category and all its subcategories?")) { await api.delete(`/categories/${cat.id}`); reload(); } }} className="text-[#EF4444] mx-1" title="Sil"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <form onSubmit={save} className="bg-white border border-border p-6 h-fit">
          <h3 className="font-heading text-xl mb-4">{editing ? (lang === "tr" ? "Düzenle" : "Edit") : (lang === "tr" ? "Yeni Kategori" : "New Category")}</h3>
          <div className="mb-3">
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">{lang === "tr" ? "Üst Kategori" : "Parent Category"}</label>
            <select data-testid="cat-field-parent" value={form.parent_id || ""} onChange={(e) => setForm({ ...form, parent_id: e.target.value || null })}
              className="w-full border-b border-[#0A0A0A] bg-transparent py-1.5 text-sm focus:outline-none">
              <option value="">{lang === "tr" ? "— Üst seviye —" : "— Top level —"}</option>
              {parents.filter((p) => p.id !== editing).map((p) => (
                <option key={p.id} value={p.id}>{p.name_tr}</option>
              ))}
            </select>
          </div>
          {[["name_tr", "Ad (TR)"], ["name_en", "Name (EN)"], ["slug", "Slug"]].map(([k, l]) => (
            <div key={k} className="mb-3">
              <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">{l}</label>
              <input data-testid={`cat-field-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required
                className="w-full border-b border-[#0A0A0A] bg-transparent py-1.5 text-sm focus:outline-none focus:border-[#C8A96A]" />
            </div>
          ))}
          <div className="mb-3">
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">Görsel</label>
            <ImagePicker value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} folder="categories" testid="cat-image-picker" />
          </div>
          <div className="mb-4">
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">{lang === "tr" ? "Sıra" : "Order"}</label>
            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0") })}
              className="w-full border-b border-[#0A0A0A] bg-transparent py-1.5 text-sm focus:outline-none" />
          </div>
          <div className="flex gap-2">
            <button data-testid="cat-save-btn" className="flex-1 bg-[#0A0A0A] hover:bg-[#C8A96A] text-white py-3 text-xs uppercase tracking-[0.2em] transition-colors">{lang === "tr" ? "Kaydet" : "Save"}</button>
            {editing && <button type="button" onClick={() => { setForm(empty); setEditing(null); }} className="border border-[#0A0A0A] px-4 text-xs uppercase">İptal</button>}
          </div>
        </form>
      </div>
    </div>
  );
}

function ProductsAdmin() {
  const { lang } = useLang();
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const fileInput = useRef(null);

  const empty = { name_tr: "", name_en: "", description_tr: "", description_en: "", sku: "", code: "", category_id: "", price: 0, discounted_price: null, stock: 0, images: [], features: [], is_featured: false, is_new: false, is_bestseller: false, status: "draft" };
  const [form, setForm] = useState(empty);

  const reload = async () => {
    const r = await api.get("/products?status=&limit=200");
    setList(r.data.items || []);
  };
  useEffect(() => { reload(); api.get("/categories").then((r) => setCats(r.data)); }, []);

  const uploadImage = async (files) => {
    const urls = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      urls.push(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
    }
    setForm((prev) => ({ ...prev, images: [...prev.images, ...urls] }));
    toast.success(lang === "tr" ? `${urls.length} resim yüklendi` : `${urls.length} images uploaded`);
  };

  const runAI = async (file) => {
    setAiLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ai/classify-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAiSuggestion(data);
      toast.success("AI suggestion ready");
    } catch (err) {
      toast.error("AI failed: " + (err?.response?.data?.detail || ""));
    } finally { setAiLoading(false); }
  };

  const applyAI = () => {
    if (!aiSuggestion) return;
    setForm((prev) => ({
      ...prev,
      name_tr: aiSuggestion.name_tr || prev.name_tr,
      name_en: aiSuggestion.name_en || prev.name_en,
      features: aiSuggestion.tags || prev.features,
    }));
    toast.success("Applied");
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, price: parseFloat(form.price), discounted_price: form.discounted_price ? parseFloat(form.discounted_price) : null, stock: parseInt(form.stock) };
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post("/products", payload);
      toast.success("OK");
      setForm(empty); setEditing(null); setAiSuggestion(null); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="admin-products">
      <h1 className="font-heading text-4xl mb-10">{lang === "tr" ? "Ürünler" : "Products"}</h1>
      <div className="grid xl:grid-cols-[1fr_500px] gap-8">
        <div className="bg-white border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F2EC] text-[10px] uppercase tracking-[0.2em]">
              <tr>
                <th className="text-left px-3 py-3">SKU</th>
                <th className="text-left px-3 py-3">{lang === "tr" ? "Ad" : "Name"}</th>
                <th className="text-right px-3 py-3">{lang === "tr" ? "Fiyat" : "Price"}</th>
                <th className="text-right px-3 py-3">Stok</th>
                <th className="text-left px-3 py-3">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} data-testid={`prod-row-${p.sku}`} className="border-t border-border">
                  <td className="px-3 py-2.5 font-finance text-xs">{p.sku}</td>
                  <td className="px-3 py-2.5">{p.name_tr}</td>
                  <td className="px-3 py-2.5 font-finance text-right">{formatTL(p.price)}</td>
                  <td className="px-3 py-2.5 font-finance text-right">{p.stock}</td>
                  <td className="px-3 py-2.5"><span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${p.status === "published" ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#52525B]/15 text-[#52525B]"}`}>{p.status}</span></td>
                  <td className="px-3 py-2.5 text-right space-x-2">
                    <button onClick={() => { setForm({ ...empty, ...p }); setEditing(p.id); }} className="text-[#C8A96A]"><Edit size={14} /></button>
                    <button onClick={async () => { if (window.confirm("Delete?")) { await api.delete(`/products/${p.id}`); reload(); } }} className="text-[#EF4444]"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <form onSubmit={save} className="bg-white border border-border p-6 h-fit space-y-3">
          <h3 className="font-heading text-xl">{editing ? "Edit Product" : "New Product"}</h3>

          {/* AI Section */}
          <div className="bg-[#F5F2EC] p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.2em] font-medium flex items-center gap-2"><Sparkles size={12} className="text-[#C8A96A]" /> AI Asistan</span>
            </div>
            <input data-testid="ai-image-input" type="file" accept="image/*"
              onChange={(e) => e.target.files?.[0] && runAI(e.target.files[0])}
              className="text-xs w-full" />
            {aiLoading && <p className="text-xs text-[#52525B]">AI {lang === "tr" ? "analiz ediyor..." : "analyzing..."}</p>}
            {aiSuggestion && (
              <div className="text-xs space-y-1 mt-2">
                <p><b>Kategori:</b> {aiSuggestion.category}</p>
                <p><b>TR:</b> {aiSuggestion.name_tr}</p>
                <p><b>EN:</b> {aiSuggestion.name_en}</p>
                <button type="button" onClick={applyAI} className="bg-[#C8A96A] text-white px-3 py-1 text-[10px] uppercase tracking-wider mt-1">Apply</button>
              </div>
            )}
          </div>

          {/* Images with drag-reorder & main selection */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">{lang === "tr" ? "Görseller" : "Images"} <span className="text-[#71717A] normal-case">({lang === "tr" ? "sürükle ile sırala · ilk resim ana resim" : "drag to reorder · first is main"})</span></label>
            <div className="flex items-center gap-3 mb-2">
              <input ref={fileInput} type="file" accept="image/*" multiple
                onChange={(e) => e.target.files && uploadImage(Array.from(e.target.files))}
                className="text-xs flex-1" data-testid="prod-image-upload" />
              <button type="button" data-testid="prod-image-library" onClick={() => setLibraryOpen(true)}
                className="text-[10px] uppercase tracking-[0.2em] text-[#C8A96A] hover:text-[#A6864A] whitespace-nowrap">
                {lang === "tr" ? "Kütüphaneden Ekle" : "From Library"}
              </button>
            </div>
            {libraryOpen && (
              <MediaLibrary mode="picker" folder="products"
                onSelect={(url) => setForm((prev) => ({ ...prev, images: [...prev.images, url] }))}
                onClose={() => setLibraryOpen(false)} />
            )}
            <div className="flex flex-wrap gap-2" data-testid="image-gallery">
              {form.images.map((img, i) => (
                <div
                  key={img + i}
                  data-testid={`prod-image-${i}`}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData("idx", String(i)); }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = parseInt(e.dataTransfer.getData("idx"));
                    if (Number.isNaN(from) || from === i) return;
                    const arr = [...form.images];
                    const [moved] = arr.splice(from, 1);
                    arr.splice(i, 0, moved);
                    setForm({ ...form, images: arr });
                  }}
                  className={`relative w-20 h-20 bg-[#F5F2EC] overflow-hidden cursor-move group ${i === 0 ? "ring-2 ring-[#C8A96A]" : "ring-1 ring-border"}`}
                >
                  <img src={img} className="w-full h-full object-cover" alt="" />
                  {i === 0 && (
                    <span className="absolute top-0 left-0 bg-[#C8A96A] text-white text-[8px] px-1 uppercase tracking-wider">{lang === "tr" ? "Ana" : "Main"}</span>
                  )}
                  {i !== 0 && (
                    <button type="button" data-testid={`make-main-${i}`}
                      onClick={() => {
                        const arr = [...form.images];
                        const [m] = arr.splice(i, 1);
                        arr.unshift(m);
                        setForm({ ...form, images: arr });
                      }}
                      className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] uppercase tracking-wider py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      {lang === "tr" ? "Ana yap" : "Make main"}
                    </button>
                  )}
                  <button type="button" data-testid={`remove-image-${i}`} onClick={() => setForm({ ...form, images: form.images.filter((_, j) => j !== i) })}
                    className="absolute top-0 right-0 bg-white/90 p-0.5"><Trash2 size={10} /></button>
                </div>
              ))}
            </div>
          </div>

          {[["name_tr", "Ad (TR)"], ["name_en", "Name (EN)"], ["sku", "SKU"], ["code", "Code"]].map(([k, l]) => (
            <div key={k}>
              <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">{l}</label>
              <input data-testid={`prod-field-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required
                className="w-full border-b border-[#0A0A0A] bg-transparent py-1.5 text-sm focus:outline-none" />
            </div>
          ))}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">Kategori</label>
            <select data-testid="prod-field-category" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} required
              className="w-full border-b border-[#0A0A0A] bg-transparent py-1.5 text-sm focus:outline-none">
              <option value="">--</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name_tr}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <NumField label="Price" testid="prod-field-price" value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
            <NumField label="Discount" testid="prod-field-discount" value={form.discounted_price || ""} onChange={(v) => setForm({ ...form, discounted_price: v })} />
            <NumField label="Stock" testid="prod-field-stock" value={form.stock} onChange={(v) => setForm({ ...form, stock: v })} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">Description (TR)</label>
            <textarea value={form.description_tr} onChange={(e) => setForm({ ...form, description_tr: e.target.value })} rows={2}
              className="w-full border border-border bg-transparent p-2 text-sm focus:outline-none focus:border-[#C8A96A]" />
          </div>
          <div className="flex flex-wrap gap-3 text-xs">
            <label><input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} /> Featured</label>
            <label><input type="checkbox" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })} /> New</label>
            <label><input type="checkbox" checked={form.is_bestseller} onChange={(e) => setForm({ ...form, is_bestseller: e.target.checked })} /> Bestseller</label>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">Status</label>
            <select data-testid="prod-field-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full border-b border-[#0A0A0A] bg-transparent py-1.5 text-sm focus:outline-none">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
          <div className="flex gap-2 pt-2">
            <button data-testid="prod-save-btn" className="flex-1 bg-[#0A0A0A] hover:bg-[#C8A96A] text-white py-3 text-xs uppercase tracking-[0.2em] transition-colors">Save</button>
            {editing && <button type="button" onClick={() => { setForm(empty); setEditing(null); setAiSuggestion(null); }} className="border border-[#0A0A0A] px-4 text-xs uppercase">Cancel</button>}
          </div>
        </form>
      </div>
    </div>
  );
}

function NumField({ label, testid, value, onChange }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">{label}</label>
      <input data-testid={testid} type="number" step="0.01" value={value || ""} onChange={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value))}
        className="w-full border-b border-[#0A0A0A] bg-transparent py-1.5 text-sm focus:outline-none" />
    </div>
  );
}

function OrdersAdmin() {
  const { lang } = useLang();
  const [orders, setOrders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const reload = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { reload(); }, []);
  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    api.get(`/admin/orders/${selectedId}`).then((r) => setDetail(r.data));
  }, [selectedId]);

  const STATUSES = ["new", "preparing", "processing", "awaiting_shipment", "shipped", "out_for_delivery", "delivered", "completed", "cancelled", "returned", "refunded"];
  const STATUS_COLOR = {
    new: "#3B82F6", preparing: "#8B5CF6", processing: "#F59E0B", awaiting_shipment: "#F97316",
    shipped: "#06B6D4", out_for_delivery: "#0EA5E9", delivered: "#10B981",
    completed: "#10B981", cancelled: "#EF4444", returned: "#71717A", refunded: "#71717A"
  };

  const updateStatus = async (oid, status) => {
    await api.put(`/admin/orders/${oid}/status`, { status });
    toast.success(lang === "tr" ? "Durum güncellendi" : "Status updated");
    reload();
    if (selectedId === oid) {
      const r = await api.get(`/admin/orders/${oid}`);
      setDetail(r.data);
    }
  };

  const deleteOrder = async (oid, opts = {}) => {
    const msg = lang === "tr"
      ? `#${oid.slice(0, 8)} siparişi KALICI olarak silinecek. Bu işlem geri alınamaz. Devam edilsin mi?`
      : `Order #${oid.slice(0, 8)} will be permanently deleted. This cannot be undone. Continue?`;
    if (!opts.skipConfirm && !window.confirm(msg)) return;
    try {
      await api.delete(`/admin/orders/${oid}`);
      toast.success(lang === "tr" ? "Sipariş silindi" : "Order deleted");
      setSelectedId(null);
      reload();
    } catch (e) {
      toast.error((lang === "tr" ? "Silme hatası: " : "Delete failed: ") + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div data-testid="admin-orders">
      <h1 className="font-heading text-4xl mb-2">{lang === "tr" ? "Siparişler" : "Orders"}</h1>
      <p className="text-sm text-[#52525B] mb-8">{lang === "tr" ? "Sipariş yönetimi ve durum takibi" : "Manage orders and track status"}</p>
      <div className="bg-white border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F2EC] text-[10px] uppercase tracking-[0.2em]">
            <tr>
              <th className="text-left px-3 py-3">#</th>
              <th className="text-left px-3 py-3">{lang === "tr" ? "Müşteri" : "Customer"}</th>
              <th className="text-right px-3 py-3">{lang === "tr" ? "Toplam" : "Total"}</th>
              <th className="text-left px-3 py-3">{lang === "tr" ? "Durum" : "Status"}</th>
              <th className="text-left px-3 py-3">{lang === "tr" ? "Tarih" : "Date"}</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} data-testid={`order-row-${o.id}`} className="border-t border-border hover:bg-[#FAF8F3] cursor-pointer" onClick={() => setSelectedId(o.id)}>
                <td className="px-3 py-2.5 font-finance text-xs">#{o.id.slice(0, 8)}</td>
                <td className="px-3 py-2.5">{o.user_email}{o.is_guest && <span className="text-[9px] uppercase ml-2 text-[#C8A96A]">Guest</span>}</td>
                <td className="px-3 py-2.5 font-finance text-right">{formatTL(o.total)}</td>
                <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <select value={o.status} data-testid={`order-status-${o.id}`}
                    onChange={(e) => updateStatus(o.id, e.target.value)}
                    style={{ color: STATUS_COLOR[o.status] || "#222" }}
                    className="text-xs border border-border px-2 py-1 bg-white font-medium">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2.5 text-xs">{new Date(o.created_at).toLocaleDateString("tr-TR")}</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <button data-testid={`order-detail-${o.id}`} onClick={() => setSelectedId(o.id)} className="text-[#C8A96A] text-xs uppercase tracking-wider mr-3">{lang === "tr" ? "Detay" : "View"}</button>
                  <button data-testid={`order-delete-${o.id}`} onClick={() => deleteOrder(o.id)} title={lang === "tr" ? "Kalıcı Sil" : "Permanent Delete"}
                    className="text-[#EF4444] hover:text-[#B91C1C] inline-flex"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Order Detail Drawer */}
      {selectedId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setSelectedId(null)}>
          <div data-testid="order-detail-modal" className="bg-white w-full max-w-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {!detail ? <div className="p-8">Loading...</div> : (
              <div className="p-8">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#C8A96A]">{lang === "tr" ? "Sipariş" : "Order"}</p>
                    <h2 className="font-heading text-3xl">#{detail.id.slice(0, 8)}</h2>
                    <p className="text-xs text-[#52525B] mt-1">{new Date(detail.created_at).toLocaleString("tr-TR")}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid="order-delete-btn" onClick={() => deleteOrder(detail.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white text-[10px] uppercase tracking-[0.2em]">
                      <Trash2 size={12} /> {lang === "tr" ? "Kalıcı Sil" : "Delete"}
                    </button>
                    <button onClick={() => setSelectedId(null)} className="p-2 hover:bg-[#FAF8F3]"><X size={20} /></button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 text-sm">
                  <div className="bg-[#FAF8F3] p-4">
                    <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-3">{lang === "tr" ? "Müşteri" : "Customer"}</h3>
                    <p>{detail.address?.full_name}</p>
                    <p className="text-[#52525B]">{detail.user_email}</p>
                    {detail.guest_email && detail.is_guest && <p className="text-[#52525B]">{detail.guest_email}</p>}
                    <p className="text-[#52525B]">{detail.address?.phone}</p>
                  </div>
                  <div className="bg-[#FAF8F3] p-4">
                    <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-3">{lang === "tr" ? "Adres" : "Address"}</h3>
                    <p>{detail.address?.address}</p>
                    <p className="text-[#52525B]">{detail.address?.district}, {detail.address?.city} {detail.address?.postal_code}</p>
                  </div>
                  <div className="bg-[#FAF8F3] p-4">
                    <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-3">{lang === "tr" ? "Ödeme" : "Payment"}</h3>
                    <p className="capitalize">{detail.payment_method?.replace("_", " ")}</p>
                    <p className="text-[#52525B] text-xs mt-1">{lang === "tr" ? "Ödeme Durumu" : "Status"}: {detail.payment_status || (lang === "tr" ? "Beklemede" : "Pending")}</p>
                  </div>
                  <div className="bg-[#FAF8F3] p-4">
                    <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-3">{lang === "tr" ? "Sipariş Durumu" : "Order Status"}</h3>
                    <select value={detail.status} data-testid="detail-status-select"
                      onChange={(e) => updateStatus(detail.id, e.target.value)}
                      className="w-full border border-border px-2 py-1.5 bg-white text-sm font-medium" style={{ color: STATUS_COLOR[detail.status] || "#222" }}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                {detail.notes && (
                  <div className="bg-[#FAF8F3] p-4 mb-6">
                    <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-2">{lang === "tr" ? "Notlar" : "Notes"}</h3>
                    <p className="text-sm">{detail.notes}</p>
                  </div>
                )}

                <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-3">{lang === "tr" ? "Ürünler" : "Items"}</h3>
                <div className="border border-border divide-y divide-border mb-6">
                  {(detail.items || []).map((it, idx) => (
                    <div key={idx} className="p-3 flex items-center gap-3">
                      {it.image && <img src={it.image} alt="" className="w-14 h-14 object-cover bg-[#FAF8F3]" />}
                      <div className="flex-1">
                        <p className="font-medium text-sm">{it.name_tr || `Product ${it.product_id?.slice(0, 8)}`}</p>
                        <p className="text-xs text-[#71717A] font-finance">{it.sku} · {lang === "tr" ? "Adet" : "Qty"}: {it.quantity}</p>
                      </div>
                      <p className="font-finance text-sm">{formatTL((it.price || 0) * (it.quantity || 1))}</p>
                    </div>
                  ))}
                </div>

                <div className="border-t border-border pt-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span>{lang === "tr" ? "Ara Toplam" : "Subtotal"}</span><span className="font-finance">{formatTL(detail.subtotal)}</span></div>
                  <div className="flex justify-between text-[#52525B]"><span>{lang === "tr" ? "Kargo" : "Shipping"}</span><span className="font-finance">{formatTL(detail.shipping || 0)}</span></div>
                  <div className="flex justify-between text-lg pt-2 border-t border-border"><span className="font-medium">{lang === "tr" ? "Toplam" : "Total"}</span><span className="font-finance text-[#C8A96A]">{formatTL(detail.total)}</span></div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CustomersAdmin() {
  const { lang } = useLang();
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState("");
  const reload = () => api.get("/admin/customers").then((r) => setList(r.data));
  useEffect(() => { reload(); }, []);
  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    api.get(`/admin/customers/${selectedId}`).then((r) => setDetail(r.data));
  }, [selectedId]);

  const filtered = list.filter((u) => !search ||
    (u.email || "").toLowerCase().includes(search.toLowerCase()) ||
    (u.name || "").toLowerCase().includes(search.toLowerCase())
  );

  const deleteCustomer = async (cid, email) => {
    const msg = lang === "tr"
      ? `${email || cid.slice(0, 8)} adlı müşteri ve TÜM siparişleri, adresleri KALICI olarak silinecek. Bu işlem geri alınamaz. Devam edilsin mi?`
      : `Customer ${email || cid.slice(0, 8)} and ALL their orders/addresses will be permanently deleted. This cannot be undone. Continue?`;
    if (!window.confirm(msg)) return;
    try {
      const res = await api.delete(`/admin/customers/${cid}`);
      toast.success((lang === "tr" ? "Silindi · " : "Deleted · ") + (res.data?.orders_deleted || 0) + (lang === "tr" ? " sipariş kaldırıldı" : " orders removed"));
      setSelectedId(null);
      reload();
    } catch (e) {
      toast.error((lang === "tr" ? "Silme hatası: " : "Delete failed: ") + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div data-testid="admin-customers">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-4xl mb-2">{lang === "tr" ? "Müşteriler" : "Customers"}</h1>
          <p className="text-sm text-[#52525B]">{lang === "tr" ? "Toplam: " : "Total: "}{list.length}</p>
        </div>
        <div className="flex items-center gap-2 border border-border bg-white px-3 py-2">
          <SearchIcon size={14} className="text-[#71717A]" />
          <input data-testid="customer-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={lang === "tr" ? "Ara..." : "Search..."}
            className="text-sm focus:outline-none bg-transparent w-48" />
        </div>
      </div>
      <div className="bg-white border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F2EC] text-[10px] uppercase tracking-[0.2em]">
            <tr>
              <th className="text-left px-3 py-3">{lang === "tr" ? "Ad" : "Name"}</th>
              <th className="text-left px-3 py-3">Email</th>
              <th className="text-left px-3 py-3">{lang === "tr" ? "Kayıt" : "Joined"}</th>
              <th className="text-left px-3 py-3">{lang === "tr" ? "Son Giriş" : "Last Login"}</th>
              <th className="text-left px-3 py-3">IP</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} data-testid={`cust-row-${u.id}`} className="border-t border-border hover:bg-[#FAF8F3] cursor-pointer" onClick={() => setSelectedId(u.id)}>
                <td className="px-3 py-2.5">{u.name}</td>
                <td className="px-3 py-2.5">{u.email}</td>
                <td className="px-3 py-2.5 text-xs text-[#52525B]">{u.created_at ? new Date(u.created_at).toLocaleDateString("tr-TR") : "—"}</td>
                <td className="px-3 py-2.5 text-xs text-[#52525B]">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString("tr-TR") : "—"}</td>
                <td className="px-3 py-2.5 text-xs text-[#71717A] font-finance">{u.last_login_ip || "—"}</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => setSelectedId(u.id)} data-testid={`cust-view-${u.id}`} className="text-[#C8A96A] text-xs uppercase tracking-wider mr-3">{lang === "tr" ? "Detay" : "View"}</button>
                  <button data-testid={`cust-delete-${u.id}`} onClick={() => deleteCustomer(u.id, u.email)}
                    title={lang === "tr" ? "Kalıcı Sil" : "Permanent Delete"}
                    className="text-[#EF4444] hover:text-[#B91C1C] inline-flex"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setSelectedId(null)}>
          <div data-testid="customer-detail-modal" className="bg-white w-full max-w-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {!detail ? <div className="p-8">Loading...</div> : (
              <div className="p-8">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#C8A96A]">{lang === "tr" ? "Müşteri" : "Customer"}</p>
                    <h2 className="font-heading text-3xl">{detail.user?.name}</h2>
                    <p className="text-sm text-[#52525B]">{detail.user?.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid="cust-delete-btn" onClick={() => deleteCustomer(detail.user?.id, detail.user?.email)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white text-[10px] uppercase tracking-[0.2em]">
                      <Trash2 size={12} /> {lang === "tr" ? "Kalıcı Sil" : "Delete"}
                    </button>
                    <button onClick={() => setSelectedId(null)} className="p-2 hover:bg-[#FAF8F3]"><X size={20} /></button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <Stat label={lang === "tr" ? "Toplam Sipariş" : "Total Orders"} value={detail.stats?.total_orders} />
                  <Stat label={lang === "tr" ? "Toplam Harcama" : "Total Spending"} value={formatTL(detail.stats?.total_spending || 0)} />
                  <Stat label={lang === "tr" ? "Kayıt" : "Joined"} value={detail.stats?.registration_date ? new Date(detail.stats.registration_date).toLocaleDateString("tr-TR") : "—"} />
                  <Stat label={lang === "tr" ? "Son Giriş" : "Last Login"} value={detail.stats?.last_login_at ? new Date(detail.stats.last_login_at).toLocaleString("tr-TR") : "—"} />
                  <Stat label={lang === "tr" ? "Şehir" : "City"} value={detail.stats?.city || "—"} />
                  <Stat label="IP" value={detail.stats?.last_login_ip || "—"} />
                </div>
                {detail.addresses?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-3">{lang === "tr" ? "Adresler" : "Addresses"}</h3>
                    <div className="space-y-2">
                      {detail.addresses.map((a) => (
                        <div key={a.id} className="bg-[#FAF8F3] p-3 text-sm">
                          <p className="font-medium">{a.title} · {a.full_name}</p>
                          <p className="text-[#52525B] text-xs">{a.address}, {a.district}, {a.city}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-3">{lang === "tr" ? "Sipariş Geçmişi" : "Order History"}</h3>
                <div className="border border-border divide-y divide-border">
                  {(detail.orders || []).length === 0 ? (
                    <p className="p-4 text-sm text-[#71717A]">{lang === "tr" ? "Sipariş yok" : "No orders"}</p>
                  ) : detail.orders.map((o) => (
                    <div key={o.id} className="p-3 flex justify-between items-center text-sm">
                      <div>
                        <p className="font-finance text-xs">#{o.id.slice(0, 8)}</p>
                        <p className="text-xs text-[#71717A]">{new Date(o.created_at).toLocaleDateString("tr-TR")}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-finance">{formatTL(o.total)}</p>
                        <p className="text-[10px] uppercase tracking-wider text-[#C8A96A]">{o.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#FAF8F3] p-3">
      <p className="text-[9px] uppercase tracking-[0.25em] text-[#52525B]">{label}</p>
      <p className="font-finance text-base mt-1">{value}</p>
    </div>
  );
}

function SeoAdmin() {
  const { lang } = useLang();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/settings/seo").then((r) => setForm(r.data)); }, []);
  if (!form) return <div>Loading...</div>;
  const save = async () => {
    setLoading(true);
    try { await api.put("/settings/seo", form); toast.success("SEO ayarları kaydedildi"); }
    catch (e) { toast.error("Hata"); }
    finally { setLoading(false); }
  };
  const Field = ({ k, label, type = "text" }) => (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">{label}</label>
      {type === "textarea" ? (
        <textarea data-testid={`seo-${k}`} rows={2} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
          className="w-full border border-border bg-transparent p-2 text-sm focus:outline-none focus:border-[#C8A96A]" />
      ) : (
        <input data-testid={`seo-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
          className="w-full border-b border-[#222] bg-transparent py-1.5 text-sm focus:outline-none focus:border-[#C8A96A]" />
      )}
    </div>
  );
  return (
    <div data-testid="admin-seo">
      <h1 className="font-heading text-4xl mb-2">{lang === "tr" ? "SEO Ayarları" : "SEO Settings"}</h1>
      <p className="text-sm text-[#52525B] mb-8">{lang === "tr" ? "Arama motoru optimizasyonu ve analitik entegrasyonları" : "Search engine optimization & analytics integrations"}</p>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-border p-6 space-y-4">
          <h3 className="font-heading text-xl">{lang === "tr" ? "Anasayfa SEO" : "Homepage SEO"}</h3>
          <Field k="homepage_title_tr" label="Title (TR)" />
          <Field k="homepage_title_en" label="Title (EN)" />
          <Field k="homepage_description_tr" label="Description (TR)" type="textarea" />
          <Field k="homepage_description_en" label="Description (EN)" type="textarea" />
          <Field k="homepage_keywords" label="Keywords" />
        </div>
        <div className="bg-white border border-border p-6 space-y-4">
          <h3 className="font-heading text-xl">{lang === "tr" ? "Open Graph & Sosyal" : "Open Graph & Social"}</h3>
          <Field k="og_title" label="OG Title" />
          <Field k="og_description" label="OG Description" type="textarea" />
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">OG Image</label>
            <ImagePicker value={form.og_image} onChange={(v) => setForm({ ...form, og_image: v })} folder="seo" testid="seo-og-image" />
          </div>
          <Field k="canonical_url" label="Canonical URL" />
          <Field k="robots" label="Robots Meta" />
        </div>
        <div className="bg-white border border-border p-6 space-y-4">
          <h3 className="font-heading text-xl">Twitter / X</h3>
          <Field k="twitter_title" label="Twitter Title" />
          <Field k="twitter_description" label="Twitter Description" type="textarea" />
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">Twitter Image</label>
            <ImagePicker value={form.twitter_image} onChange={(v) => setForm({ ...form, twitter_image: v })} folder="seo" testid="seo-twitter-image" />
          </div>
        </div>
        <div className="bg-white border border-border p-6 space-y-4">
          <h3 className="font-heading text-xl">{lang === "tr" ? "Analitik Entegrasyonu" : "Analytics Integration"}</h3>
          <Field k="google_site_verification" label="Google Search Console Verification" />
          <Field k="google_analytics_id" label="Google Analytics 4 (GA-XXX-Y)" />
          <Field k="google_tag_manager_id" label="Google Tag Manager (GTM-XXXX)" />
        </div>
        <div className="bg-white border border-border p-6 space-y-4">
          <h3 className="font-heading text-xl">{lang === "tr" ? "Robots & Sitemap" : "Robots & Sitemap"}</h3>
          <Field k="custom_robots_txt" label="Custom robots.txt content (leave empty for default)" type="textarea" />
          <p className="text-xs text-[#52525B]"><a href="/api/sitemap.xml" target="_blank" rel="noopener noreferrer" className="text-[#C8A96A] underline">{lang === "tr" ? "Sitemap'ı görüntüle" : "View Sitemap"}</a></p>
          <p className="text-xs text-[#52525B]"><a href="/api/robots.txt" target="_blank" rel="noopener noreferrer" className="text-[#C8A96A] underline">{lang === "tr" ? "robots.txt'i görüntüle" : "View robots.txt"}</a></p>
        </div>
      </div>
      <button data-testid="seo-save-btn" disabled={loading} onClick={save} className="mt-8 bg-[#222] hover:bg-[#C8A96A] text-white px-10 py-3 text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-50">
        {loading ? "..." : (lang === "tr" ? "Kaydet" : "Save")}
      </button>
    </div>
  );
}

function RatesAdmin() {
  const [margins, setMargins] = useState(null);
  const [rates, setRates] = useState(null);
  const reload = async () => {
    const [m, r] = await Promise.all([api.get("/settings/margins"), api.get("/rates")]);
    setMargins({ ...m.data, items: m.data.items || {} });
    setRates(r.data);
  };
  useEffect(() => { reload(); }, []);
  if (!margins || !rates) return <div>Loading...</div>;

  const updateItem = (key, field, value) => {
    const items = { ...(margins.items || {}) };
    items[key] = { ...(items[key] || { buy: 0, sell: 0 }), [field]: parseFloat(value || "0") };
    setMargins({ ...margins, items });
  };
  const itemMargin = (key, field) => {
    const v = (margins.items || {})[key]?.[field];
    return v == null ? 0 : v;
  };

  const save = async () => {
    await api.put("/settings/margins", margins);
    toast.success("Saved");
    await reload();
  };
  const refresh = async () => {
    await api.post("/rates/refresh");
    toast.success("Refreshed");
    await reload();
  };

  const Section = ({ title, items, keyField, nameField }) => (
    <div className="bg-white border border-border">
      <div className="px-5 py-3 bg-[#0A0A0A] text-white flex items-center justify-between">
        <h3 className="font-heading text-lg">{title}</h3>
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#C8A96A]">{items.length} {items.length === 1 ? "item" : "items"}</span>
      </div>
      <div className="grid grid-cols-[1fr_auto_auto] px-5 py-2 bg-[#F5F2EC] text-[10px] uppercase tracking-[0.2em] text-[#52525B]">
        <span>Ürün / Kod</span>
        <span className="text-right pr-2 w-32">Alış Marjı %</span>
        <span className="text-right w-32">Satış Marjı %</span>
      </div>
      <div className="divide-y divide-border">
        {items.map((r) => {
          const key = r[keyField];
          const name = nameField ? r[nameField] : r.code;
          return (
            <div key={key} data-testid={`margin-row-${key}`} className="grid grid-cols-[1fr_auto_auto] px-5 py-2.5 items-center text-sm">
              <span>{name} <span className="font-finance text-xs text-[#71717A] ml-2">{r.code || ""}</span></span>
              <input data-testid={`margin-buy-${key}`} type="number" step="0.01" value={itemMargin(key, "buy")}
                onChange={(e) => updateItem(key, "buy", e.target.value)}
                className="w-28 border border-border bg-transparent py-1 px-2 text-sm text-right font-finance focus:outline-none focus:border-[#C8A96A]" />
              <input data-testid={`margin-sell-${key}`} type="number" step="0.01" value={itemMargin(key, "sell")}
                onChange={(e) => updateItem(key, "sell", e.target.value)}
                className="w-28 ml-2 border border-border bg-transparent py-1 px-2 text-sm text-right font-finance focus:outline-none focus:border-[#C8A96A]" />
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div data-testid="admin-rates">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-4xl mb-2">Döviz & Altın Marjları</h1>
          <p className="text-sm text-[#52525B]">Her döviz ve altın ürünü için ayrı alış/satış marjı tanımlayın</p>
        </div>
        <div className="flex gap-3">
          <button onClick={refresh} data-testid="rates-refresh-btn" className="border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white px-5 py-3 text-xs uppercase tracking-[0.2em] transition-colors">Şimdi Yenile</button>
          <button data-testid="margins-save-btn" onClick={save} className="bg-[#0A0A0A] hover:bg-[#C8A96A] text-white px-8 py-3 text-xs uppercase tracking-[0.2em] transition-colors">Tüm Marjları Kaydet</button>
        </div>
      </div>

      <div className="bg-[#F5F2EC] p-5 mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-1">Gösterim Modu</p>
          <select data-testid="display-mode" value={margins.display_mode} onChange={(e) => setMargins({ ...margins, display_mode: e.target.value })}
            className="border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#C8A96A]">
            <option value="raw">RAW · Sağlayıcı değerleri (TCMB / Nadir Döviz)</option>
            <option value="markup">MARKUP · Marjlı gösterim</option>
          </select>
        </div>
        <p className="text-xs text-[#52525B] max-w-md">RAW modunda marjlar uygulanmaz. MARKUP modunda her satırdaki marj o ürüne ayrı uygulanır.</p>
      </div>

      <div className="grid xl:grid-cols-3 gap-6">
        <Section title="DÖVİZ" items={rates.currencies || []} keyField="code" nameField="name" />
        <Section title="ALTIN" items={rates.gold || []} keyField="key" nameField="name_tr" />
        <Section title="GÜMÜŞ" items={rates.silver || []} keyField="key" nameField="name_tr" />
      </div>
    </div>
  );
}

function SiteAdmin() {
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { api.get("/settings/site").then((r) => setForm(r.data)); }, []);
  if (!form) return <div>Loading...</div>;

  const save = async () => { await api.put("/settings/site", form); toast.success("Site ayarları kaydedildi"); };

  const uploadLogo = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm({ ...form, logo_url: fullUrl });
      toast.success("Logo yüklendi");
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const fields = [
    ["instagram_url", "Instagram URL"],
    ["address_line1", "Adres Satırı 1"],
    ["address_line2", "Adres Satırı 2"],
    ["phone", "Telefon"],
    ["email", "E-posta"],
    ["map_embed_src", "Google Maps Embed URL"],
    ["map_directions_url", "Google Maps Yol Tarifi URL"],
    ["founded_year", "Kuruluş Yılı"],
    ["whatsapp_number", "WhatsApp Numarası (örn. +905368147467)"],
    ["new_arrival_days", "Yeni Etiketi Süresi (gün)"],
  ];

  return (
    <div data-testid="admin-site">
      <h1 className="font-heading text-4xl mb-2">Site Ayarları</h1>
      <p className="text-sm text-[#52525B] mb-10">Logo, adres, Instagram, harita ve iletişim bilgilerini yönetin</p>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-border p-6">
          <h3 className="font-heading text-xl mb-2">Logo</h3>
          <p className="text-xs text-[#52525B] mb-3">PNG, SVG veya WebP. Header, footer ve mobil menüde gösterilir.</p>
          {form.logo_url && (
            <div className="mb-4 inline-block bg-[#FAF8F3] p-3">
              <img src={form.logo_url} alt="Logo" className="h-20 w-20 object-contain" />
            </div>
          )}
          <ImagePicker value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} folder="logo" testid="logo-picker" />
        </div>
        <div className="bg-white border border-border p-6">
          <h3 className="font-heading text-xl mb-2">Favicon</h3>
          <p className="text-xs text-[#52525B] mb-3">Tarayıcı sekmesinde gösterilir. ICO, PNG veya SVG. Logodan bağımsız.</p>
          {form.favicon_url && (
            <div className="mb-4 inline-block bg-[#FAF8F3] p-3">
              <img src={form.favicon_url} alt="Favicon" className="h-12 w-12 object-contain" />
            </div>
          )}
          <ImagePicker value={form.favicon_url} onChange={(v) => setForm({ ...form, favicon_url: v })} folder="favicon" testid="favicon-picker" />
        </div>
        <div className="bg-white border border-border p-6 space-y-3 lg:col-span-2">
          <h3 className="font-heading text-xl mb-4">İletişim & Lokasyon</h3>
          {fields.map(([k, l]) => (
            <div key={k}>
              <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">{l}</label>
              <input data-testid={`site-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="w-full border-b border-[#222] bg-transparent py-1.5 text-sm focus:outline-none focus:border-[#C8A96A]" />
            </div>
          ))}
        </div>
      </div>
      {form.map_embed_src && (
        <div className="mt-8 bg-white border border-border p-6">
          <h3 className="font-heading text-xl mb-4">Harita Önizleme</h3>
          <div className="aspect-video">
            <iframe src={form.map_embed_src} className="w-full h-full border-0" loading="lazy" title="Map preview" />
          </div>
        </div>
      )}
      <button data-testid="site-save-btn" onClick={save} className="mt-8 bg-[#0A0A0A] hover:bg-[#C8A96A] text-white px-10 py-3 text-xs uppercase tracking-[0.2em] transition-colors">Kaydet</button>
    </div>
  );
}

function HomepageAdmin() {
  const [form, setForm] = useState(null);
  useEffect(() => { api.get("/settings/homepage").then((r) => setForm(r.data)); }, []);
  if (!form) return <div>Loading...</div>;
  const save = async () => { await api.put("/settings/homepage", form); toast.success("Saved"); };
  const fields = [["hero_title_tr", "Hero Başlık (TR)"], ["hero_title_en", "Hero Title (EN)"], ["hero_subtitle_tr", "Hero Alt Başlık (TR)"], ["hero_subtitle_en", "Hero Subtitle (EN)"], ["about_tr", "Hakkımızda (TR)"], ["about_en", "About (EN)"], ["instagram_handle", "Instagram"]];
  return (
    <div data-testid="admin-homepage">
      <h1 className="font-heading text-4xl mb-10">Anasayfa İçeriği</h1>
      <div className="bg-white border border-border p-6 max-w-3xl space-y-4">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">Hero Arka Plan Görseli</label>
          <ImagePicker value={form.hero_image} onChange={(v) => setForm({ ...form, hero_image: v })} folder="homepage" testid="hp-hero-image" />
        </div>
        {fields.map(([k, l]) => (
          <div key={k}>
            <label className="block text-[10px] uppercase tracking-[0.2em] mb-1">{l}</label>
            <textarea data-testid={`hp-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} rows={k.includes("about") ? 3 : 1}
              className="w-full border border-border bg-transparent p-2 text-sm focus:outline-none focus:border-[#C8A96A]" />
          </div>
        ))}
        <button data-testid="hp-save-btn" onClick={save} className="bg-[#0A0A0A] hover:bg-[#C8A96A] text-white px-8 py-3 text-xs uppercase tracking-[0.2em] transition-colors">Kaydet</button>
      </div>
    </div>
  );
}
